import { FC, useEffect, useState } from 'react';
import type { AdminUserListItem } from '@libs/types';
import { Avatar, Box, Button, IconButton, InputBase, Stack, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ConfirmationModal, ListPagination } from '@components';
import { useTrans } from '@libs/i18n';
import { KeyIcon, MagnifyingGlassIcon, PencilSimpleIcon, ShieldCheckIcon, SignOutIcon } from '@phosphor-icons/react';

import { useAdminUsers, useForceLogoutUser } from '../api/admin-api';
import { formatDate } from '../components/adminFormat';
import { AdminStatusChip } from '../components/AdminStatusChip';
import { AdminTable } from '../components/AdminTable';
import { adminButtonSx, adminColors } from '../components/adminTheme';
import { EditableUser, EditUserModal } from '../components/EditUserModal';
import { SetPasswordModal } from '../components/SetPasswordModal';

const PAGE_SIZE = 20;

// Platform-admin granting is disabled for now; the users list only supports
// forcing a re-authentication.
type ConfirmState = { type: 'logout'; user: AdminUserListItem } | null;

const userName = (u: AdminUserListItem) =>
  `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.username;

export const AdminUsersPage: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [editUser, setEditUser] = useState<EditableUser | null>(null);
  const [pwdUser, setPwdUser] = useState<{ id: string; label: string } | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const { data, isLoading, isFetching } = useAdminUsers({ page, limit: PAGE_SIZE, search: debounced || undefined });
  const rows = data?.rows ?? [];
  const meta = data?.meta;

  const logoutUser = useForceLogoutUser();

  const columns = [
    { key: 'user', label: t('app.admin.users.table.user'), width: '22%' },
    { key: 'email', label: t('app.admin.users.table.email'), width: '22%' },
    { key: 'role', label: t('app.admin.users.table.role'), width: '14%' },
    { key: 'status', label: t('app.admin.users.table.status'), width: '10%' },
    { key: 'created', label: t('app.admin.users.table.created'), width: '14%' },
    { key: 'actions', label: t('app.admin.users.table.actions'), align: 'right' as const, width: '18%' },
  ];

  const runConfirm = () => {
    if (!confirm) return;
    logoutUser.mutate(confirm.user.id);
    setConfirm(null);
  };

  const confirmCopy = confirm
    ? {
        title: t('app.admin.users.logoutConfirm.title'),
        body: t('app.admin.users.logoutConfirm.body', { name: userName(confirm.user) }),
        cta: t('app.admin.users.logout'),
        color: 'warning' as const,
      }
    : null;

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 2.75, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2.25 }}>
        <Typography sx={{ fontSize: 23, fontWeight: 700, color: c.ink, mb: 0.375 }}>
          {t('app.admin.users.title')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: c.sub }}>{t('app.admin.users.subtitle')}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.125,
          backgroundColor: theme.palette.grey[200],
          border: `1px solid ${c.border}`,
          borderRadius: 2.5,
          px: 1.5,
          py: 1,
          mb: 1.5,
          maxWidth: 420,
        }}
      >
        <MagnifyingGlassIcon size={16} color={c.faint} />
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('app.admin.users.search')}
          sx={{ flex: 1, fontSize: 13, color: c.ink }}
        />
      </Box>

      <AdminTable
        columns={columns}
        minWidth={900}
        isLoading={isLoading}
        isEmpty={!rows.length}
        emptyLabel={t('app.admin.users.empty')}
        footer={
          meta && meta.totalCount > 0 ? (
            <ListPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              onPageChange={setPage}
              isLoading={isFetching}
              sx={{ mt: 0 }}
            />
          ) : undefined
        }
      >
        {rows.map((u) => {
          return (
            <TableRow key={u.id}>
              <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 600, background: 'linear-gradient(135deg,#623AE6,#0063B3)' }}>
                    {(userName(u) || u.email).slice(0, 2).toUpperCase()}
                  </Avatar>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.ink }}>{userName(u)}</Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>{u.email}</TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
                {u.isPlatformAdmin ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.625, backgroundColor: c.violetBg, border: `1px solid ${c.violetBorder}`, borderRadius: 999, px: 1.125, py: 0.375 }}>
                    <ShieldCheckIcon size={12} color={c.violet} weight="fill" />
                    <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: c.violet }}>
                      {t('app.admin.users.platformAdmin')}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: c.faint }}>{t('app.admin.users.tenantUser')}</Typography>
                )}
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
                <AdminStatusChip status={u.enabled ? 'active' : 'suspended'} />
              </TableCell>
              <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>
                {formatDate(u.createdTimestamp)}
              </TableCell>
              <TableCell align="right" sx={{ borderBottom: `1px solid ${c.border}` }}>
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                  <IconButton
                    onClick={() => setEditUser({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email, enabled: u.enabled })}
                    title={t('app.admin.editUser.title')}
                    sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}
                  >
                    <PencilSimpleIcon size={15} />
                  </IconButton>
                  <IconButton
                    onClick={() => setPwdUser({ id: u.id, label: userName(u) })}
                    title={t('app.admin.setPassword.title')}
                    sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: c.violetBg, color: c.violet }}
                  >
                    <KeyIcon size={15} />
                  </IconButton>
                  <Button
                    onClick={() => setConfirm({ type: 'logout', user: u })}
                    startIcon={<SignOutIcon size={14} />}
                    sx={{ ...adminButtonSx(theme).neutral, fontSize: 12, px: 1.375, py: 0.5, borderRadius: 2 }}
                  >
                    {t('app.admin.users.logout')}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
      </AdminTable>

      {confirm && confirmCopy && (
        <ConfirmationModal
          open
          onClose={() => setConfirm(null)}
          title={confirmCopy.title}
          iconColor={confirmCopy.color}
          content={<Typography sx={{ fontSize: 14, color: c.sub }}>{confirmCopy.body}</Typography>}
          buttons={[
            {
              label: t('app.admin.users.cancel'),
              onClick: () => setConfirm(null),
              variant: 'outlined',
              color: 'secondary',
            },
            {
              label: confirmCopy.cta,
              onClick: runConfirm,
              variant: 'contained',
              color: confirmCopy.color,
            },
          ]}
        />
      )}

      {editUser && <EditUserModal open onClose={() => setEditUser(null)} user={editUser} />}
      {pwdUser && (
        <SetPasswordModal open onClose={() => setPwdUser(null)} userId={pwdUser.id} userLabel={pwdUser.label} />
      )}
    </Box>
  );
});
